/**
 * Checklist Auto-Detection Utility
 *
 * This utility provides functions to automatically detect when users complete
 * checklist items and update the tour progress accordingly.
 */

import { TourActions } from "../components/api/RequestUtil";
import { store } from "../redux/store";
import { updateChecklistItems } from "../redux/features/userSlice";

/**
 * Check if user has completed their profile
 * Profile is considered complete if user has:
 * - First name
 * - Last name
 * - Profile image
 */
export const checkProfileCompletion = async () => {
  // Add delay to ensure Redux state is updated after refetch
  await new Promise((resolve) => setTimeout(resolve, 500));

  const state = store.getState();
  // Profile data is in settings.accountOwnerDetails after refetchData()
  const { firstName, lastName, profileImage } = state.settings?.accountOwnerDetails || {};
  const tourProgress = state.user?.tourProgress;

  // Only check if tour is active and item not already completed
  if (!tourProgress || tourProgress.checklist_items?.complete_profile) {
    return;
  }

  // Check if profile is complete
  const isComplete = Boolean(firstName && lastName && profileImage);

  if (isComplete) {
    try {
      await TourActions.updateChecklistItems({ complete_profile: true });
      store.dispatch(updateChecklistItems({ complete_profile: true }));
    } catch (error) {
      console.error("Error updating profile completion:", error);
    }
  }
};

/**
 * Mark "Add First Client" as complete
 * Call this after successfully creating a client
 */
export const markFirstClientAdded = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.add_first_client) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ add_first_client: true });
    store.dispatch(updateChecklistItems({ add_first_client: true }));
  } catch (error) {
    console.error("Error marking first client added:", error);
  }
};

/**
 * Mark "Create First Program" as complete
 * Call this after successfully creating/saving a program
 */
export const markFirstProgramCreated = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.create_first_program) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ create_first_program: true });
    store.dispatch(updateChecklistItems({ create_first_program: true }));
  } catch (error) {
    console.error("Error marking first program created:", error);
  }
};

/**
 * Mark "Schedule First Session" as complete
 * Call this after successfully creating a booking
 */
export const markFirstSessionScheduled = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.schedule_first_session) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ schedule_first_session: true });
    store.dispatch(updateChecklistItems({ schedule_first_session: true }));
  } catch (error) {
    console.error("Error marking first session scheduled:", error);
  }
};

/**
 * Mark "Setup Payment Connection" as complete
 * Call this after successfully connecting eWay payment
 */
export const markPaymentConnectionSetup = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_payment_connection) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_payment_connection: true });
    store.dispatch(updateChecklistItems({ setup_payment_connection: true }));
  } catch (error) {
    console.error("Error marking payment connection setup:", error);
  }
};

/**
 * Mark "Configure Communication Settings" as complete
 * Call this after user saves communication settings (timezone, notifications, etc.)
 */
export const markCommunicationSettingsComplete = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_communication) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_communication: true });
    store.dispatch(updateChecklistItems({ setup_communication: true }));
  } catch (error) {
    console.error("Error marking communication settings complete:", error);
  }
};

/**
 * Mark "Complete Initial Setup" as complete
 * Call this after user completes the setup tab
 */
export const markInitialSetupComplete = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.complete_setup) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ complete_setup: true });
    store.dispatch(updateChecklistItems({ complete_setup: true }));
  } catch (error) {
    console.error("Error marking initial setup complete:", error);
  }
};

/**
 * Mark "Set Up Subscriptions" as complete
 * Call this after user creates their first subscription/service
 */
export const markSubscriptionsSetup = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_subscriptions) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_subscriptions: true });
    store.dispatch(updateChecklistItems({ setup_subscriptions: true }));
  } catch (error) {
    console.error("Error marking subscriptions setup:", error);
  }
};

/**
 * Mark "Landing Page Setup" as complete
 * Call this after user creates their landing page form
 */
export const markLandingPageSetup = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_landing_page) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_landing_page: true });
    store.dispatch(updateChecklistItems({ setup_landing_page: true }));
  } catch (error) {
    console.error("Error marking landing page setup:", error);
  }
};

/**
 * Mark "Set Up Forms" as complete
 * Call this after user creates or configures their first form
 */
export const markFormsSetup = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_forms) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_forms: true });
    store.dispatch(updateChecklistItems({ setup_forms: true }));
  } catch (error) {
    console.error("Error marking forms setup:", error);
  }
};

/**
 * Mark "Set Up Availability" as complete
 * Call this after user configures their availability/working hours
 */
export const markAvailabilitySetup = async () => {
  const state = store.getState();
  const tourProgress = state.user?.tourProgress;

  if (!tourProgress || tourProgress.checklist_items?.setup_availability) {
    return;
  }

  try {
    await TourActions.updateChecklistItems({ setup_availability: true });
    store.dispatch(updateChecklistItems({ setup_availability: true }));
  } catch (error) {
    console.error("Error marking availability setup:", error);
  }
};

/**
 * Check all checklist items on app load
 * This ensures items are marked as complete if user already did them before the tour feature was added
 */
export const checkAllChecklistItems = async () => {
  await checkProfileCompletion();
  // Add more checks here as needed
};
